import { Component } from '@theme/component';
import { clamp, prefersReducedMotion } from '@theme/utilities';

class Parallax extends Component {
  // Per-frame catch-up, and the gap at which an item stops.
  static #EASING = 0.08;
  static #SETTLED = 0.1;

  // Where in the block's pass the climb starts and ends.
  static #PLATEAU = 0.3;
  static #RELEASE = 0.7;

  // Each item's share of the shift, dealt out in turn.
  static #SHARES = [0.2, 0.6, 0.3, 0.05, 0.5, 1];

  // Most the item at the foot keeps, so it is not pinned. Never more than its own share.
  static #RESTING = 0.08;

  static #COLUMNS = ['left', 'center', 'right'];

  #grid = null;
  #items = [];
  #heights = new Map();
  #packed = false;
  #viewportHeight = 0;
  #shift = 0;
  #frameId = null;
  #packId = null;
  #resizeObserver = null;
  #intersectionObserver = null;

  connectedCallback() {
    super.connectedCallback();
    this.#setup();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#teardown();
  }

  updatedCallback() {
    super.updatedCallback?.();
    this.#teardown();
    this.#setup();
  }

  // Resizes are watched on the items, not on the grid whose height #pack() writes itself. The
  // observer hands over measurements the browser has already taken, which is what spares #pack()
  // from asking for them again.
  #setup() {
    this.#grid = this.querySelector('.parallax-section__grid');
    if (!this.#grid) return;

    this.#items = [...this.querySelectorAll('.parallax-item')].map((element, index) => ({
      element,
      column: Parallax.#COLUMNS.findIndex((name) =>
        element.classList.contains(`parallax-item--${name}`)
      ),
      share: Parallax.#SHARES[index % Parallax.#SHARES.length],
      reach: 0,
      width: 0,
      x: 0,
      y: 0,
      offset: 0,
      paintedX: null,
      paintedY: null,
    }));
    if (!this.#items.length) return;

    this.#resizeObserver = new ResizeObserver(this.#onResize);
    this.#items.forEach(({ element }) => this.#resizeObserver.observe(element));
    window.addEventListener('resize', this.#schedulePack, { signal: this.abortController.signal });

    this.#pack();

    if (prefersReducedMotion()) return;

    this.#intersectionObserver = new IntersectionObserver(
      (entries) => (entries.some((entry) => entry.isIntersecting) ? this.#start() : this.#stop()),
      { rootMargin: '200px' }
    );
    this.#intersectionObserver.observe(this);

    window.addEventListener('scroll', this.#onScroll, {
      passive: true,
      signal: this.abortController.signal,
    });
  }

  #teardown() {
    this.#stop();
    if (this.#packId != null) cancelAnimationFrame(this.#packId);
    this.#packId = null;
    this.#intersectionObserver?.disconnect();
    this.#intersectionObserver = null;
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;
    this.#unpack();
    this.#heights.clear();
    this.#items = [];
    this.#grid = null;
  }

  #onResize = (entries) => {
    for (const entry of entries) {
      const box = entry.borderBoxSize?.[0];
      this.#heights.set(entry.target, box ? box.blockSize : entry.target.offsetHeight);
    }
    this.#schedulePack();
  };

  #schedulePack = () => {
    if (this.#packId != null) return;
    this.#packId = requestAnimationFrame(() => {
      this.#packId = null;
      this.#pack();
    });
  };

  // Stacks each column on its own, which a shared row grid cannot: its tallest item sets the row
  // height and every shorter column inherits the leftover as a hole. An item also gives up its
  // share as it nears the foot, so the block's floor stays where the layout put it. Heights are
  // only measured here when this pass changed a width, since any other pass was called by the
  // observer that had just measured them.
  #pack() {
    const style = getComputedStyle(this.#grid);
    const columns = parseInt(style.getPropertyValue('--parallax-columns'), 10) || 0;

    if (!columns) {
      this.#unpack();
      return;
    }

    this.#viewportHeight = window.innerHeight;
    this.#shift = parseFloat(style.getPropertyValue('--parallax-shift')) || 0;

    const columnGap = parseFloat(style.columnGap) || 0;
    const rowGap = parseFloat(style.rowGap) || 0;
    const stagger = [
      0,
      parseFloat(style.getPropertyValue('--parallax-stagger-center')) || 0,
      parseFloat(style.getPropertyValue('--parallax-stagger-right')) || 0,
    ];

    this.#grid.classList.add('is-packed');
    this.#packed = true;
    const width = (this.#grid.clientWidth - (columns - 1) * columnGap) / columns;

    let resized = false;
    for (const item of this.#items) {
      if (item.width === width) continue;
      item.width = width;
      item.element.style.width = `${width}px`;
      resized = true;
    }

    const heights = this.#items.map(({ element }) =>
      resized ? element.offsetHeight : this.#heights.get(element) ?? element.offsetHeight
    );

    const bottoms = new Array(columns).fill(null);
    this.#items.forEach((item, index) => {
      const column = Math.min(Math.max(item.column, 0), columns - 1);
      const top = bottoms[column] ?? stagger[Math.max(item.column, 0)] ?? 0;
      item.x = column * (width + columnGap);
      item.y = top;
      bottoms[column] = top + heights[index] + rowGap;
    });

    const height = Math.max(...bottoms.map((bottom) => (bottom ?? rowGap) - rowGap));
    this.#grid.style.height = `${height}px`;

    this.#items.forEach((item, index) => {
      const room = height - (item.y + heights[index]);
      const taper = clamp(this.#shift > 0 ? room / this.#shift : 0, 0, 1);
      item.reach = Math.max(item.share * taper, Math.min(item.share, Parallax.#RESTING));
    });

    this.#paint();
  }

  // Hands the items back to flow, which is what the mobile swipe carousel needs.
  #unpack() {
    if (!this.#packed) return;

    this.#stop();
    this.#packed = false;
    this.#grid?.classList.remove('is-packed');
    if (this.#grid) this.#grid.style.height = '';

    for (const item of this.#items) {
      item.width = 0;
      item.x = 0;
      item.y = 0;
      item.offset = 0;
      item.paintedX = null;
      item.paintedY = null;
      item.element.style.width = '';
      item.element.style.transform = '';
    }
  }

  // How far into its climb the block is, on a smoothstep so neither end arrives with a jolt. Read
  // live, because the page above keeps changing height while images land.
  #factor() {
    if (!this.#shift) return 0;

    const rect = this.getBoundingClientRect();
    const travel = this.#viewportHeight + rect.height;
    if (travel <= 0) return 0;

    const progress = clamp((this.#viewportHeight - rect.top) / travel, 0, 1);
    const spent = clamp(
      (progress - Parallax.#PLATEAU) / (Parallax.#RELEASE - Parallax.#PLATEAU),
      0,
      1
    );
    return -(spent * spent * (3 - 2 * spent));
  }

  #onScroll = () => this.#start();

  // Runs only while there is distance left to close, so a still page costs nothing.
  #start() {
    if (this.#frameId != null || !this.#packed) return;

    const step = () => {
      const reach = this.#factor() * this.#shift;
      let moving = false;

      for (const item of this.#items) {
        const distance = reach * item.reach - item.offset;
        if (Math.abs(distance) < Parallax.#SETTLED) {
          item.offset += distance;
        } else {
          item.offset += distance * Parallax.#EASING;
          moving = true;
        }
      }

      this.#paint();
      this.#frameId = moving ? requestAnimationFrame(step) : null;
    };

    this.#frameId = requestAnimationFrame(step);
  }

  #stop() {
    if (this.#frameId != null) cancelAnimationFrame(this.#frameId);
    this.#frameId = null;
  }

  // One transform carries both the packed position and the drift. An item that has not moved since
  // the last frame is left alone, which is most of them on most frames.
  #paint() {
    for (const item of this.#items) {
      const y = Math.round((item.y + item.offset) * 100) / 100;
      if (y === item.paintedY && item.x === item.paintedX) continue;

      item.paintedX = item.x;
      item.paintedY = y;
      item.element.style.transform = `translate(${item.x}px, ${y}px)`;
    }
  }
}

if (!customElements.get('parallax-component')) {
  customElements.define('parallax-component', Parallax);
}
